import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import NumberInput from 'components/inputs/NumberInput';
import Button from '@mui/material/Button';
import clsx from 'clsx';
import useDebounce from 'hooks/useDebounce';
import { getCandidateFeedbackPDF } from 'requests/EmailRequests';
import { downloadFile } from 'helper/commonFunctions';
import CandidateFeedbackButton from 'components/assessments_pages/asessment_settings_components/InstructionToCandidatesSection/components/CandidateFeedbackButton';
import InfoTooltip from 'components/tooltips/InfoTooltip';
import AssessmentSettingsEvents from 'events/AssessmentSettingsEvents';
import { Link } from 'react-router-dom';
import { saveAssessmentSettings } from 'requests/ScriptRequests';
import Checkbox from 'libraries/Checkbox';
import useStyles from './styles';


const RADIO = {
    DAYS: 'days',
    AFTER: 'after'
};

const EVENT_STRINGS = {
    closed: 'after assessment closed',
    days: 'days after last completion'
};

const CandidateFeedback = (props) => {
    const { setSaving, settings, audition, setAudition, setSettings, company, userRoleType } = props;
    const { daysToSendCandidateFeedback, sendCandidateFeedback, includeRankInCandidateFeedback,
        sendCandidateFeedbackToCompleted, sendCandidateFeedbackToHired, sendCandidateFeedbackToRejected
    } = settings;
    const { candidateFeedbackEnabled } = company;
    const { slug } = audition;

    const [settingActive, setIsSettingActive] = useState(sendCandidateFeedback);
    const [feedbackVariant, setFeedbackVariant] = useState(daysToSendCandidateFeedback === -1 ? RADIO.AFTER : 'days');
    const [daysCount, setDaysCount] = useState(daysToSendCandidateFeedback === -1 ? 7 : daysToSendCandidateFeedback);
    const [includeChecked, setIncludeChecked] = useState(includeRankInCandidateFeedback);
    const [feedbackRecipients, setFeedbackRecipients] = useState({
        completed: sendCandidateFeedbackToCompleted ?? true,
        hired: sendCandidateFeedbackToHired ?? true,
        rejected: sendCandidateFeedbackToRejected ?? true,
        all:
        (sendCandidateFeedbackToCompleted ?? true)
        && (sendCandidateFeedbackToHired ?? true)
        && (sendCandidateFeedbackToRejected ?? true)
    });

    const hasEditPermissions = (userRoleType === 'owner' || userRoleType === 'admin');

    const updateSettings = (otherSettings) => {
        setSettings({ ...settings, ...otherSettings });
    };

    const handleChangeCandidateFeedback = async (checked, otherSettings) => {
        setSaving(true);
        setIsSettingActive(checked);
        await saveAssessmentSettings(slug, { sendCandidateFeedback: checked, ...otherSettings });
        setAudition({ ...audition, sendCandidateFeedback: checked });
        updateSettings({ sendCandidateFeedback: checked, ...otherSettings });
        setSaving(false);
    };
    const handleIncludeCheckbox = async (e) => {
        const { checked } = e.target;
        setSaving(true);
        setIncludeChecked(checked);
        await saveAssessmentSettings(slug, { includeRankInCandidateFeedback: checked });
        setAudition({ ...audition, includeRankInCandidateFeedback: checked });
        setSaving(false);
        AssessmentSettingsEvents.FEEDBACK_RANK_INCLUDED({
            ttUUID: audition.uuid,
            ttName: audition.name,
            enabled: checked
        });
    };

    useDebounce(daysCount, 300, () => {
        handleChangeCandidateFeedback(true, {
            daysToSendCandidateFeedback: feedbackVariant === RADIO.AFTER ? -1 : Number(daysCount)
        });
        handleSegmentEvent(feedbackVariant);
    });

    const classes = useStyles();

    const handleRadioChange = (e) => {
        const { value } = e.target;
        setFeedbackVariant(value);
        handleChangeCandidateFeedback(true, {
            daysToSendCandidateFeedback: value === RADIO.AFTER ? -1 : daysCount
        });
        handleSegmentEvent(value);
    };

    const handleSegmentEvent = (value) => {
        const additionalEventProps = {};
        if (value === RADIO.AFTER) {
            additionalEventProps.sendingEvent = EVENT_STRINGS.closed;
        } else {
            additionalEventProps.sendingEvent = EVENT_STRINGS.days;
            additionalEventProps.daysNumber = daysCount;
        }
        AssessmentSettingsEvents.FEEDBACK_SETTING_CHANGED({
            ttUUID: audition.uuid,
            ttName: audition.name,
            ...additionalEventProps
        });
    };

    const handleSwitchChange = (e) => {
        const { checked } = e.target;
        handleChangeCandidateFeedback(checked, checked ? {
            daysToSendCandidateFeedback: daysCount
        } : {});
        AssessmentSettingsEvents.FEEDBACK_ENABLED({
            ttUUID: audition.uuid,
            ttName: audition.name,
            enabled: checked
        });
    };

    const handleChangeDays = (e) => {
        const { value } = e.target;

        let daysValue = parseInt(value, 10);
        if (Number.isNaN(daysValue) || daysValue <= 3) {
            daysValue = 3;
        }
        if (daysValue >= 30) {
            daysValue = 30;
        }

        setDaysCount(daysValue);
        updateSettings({ daysToSendCandidateFeedback: Number(daysValue) });
    };

    const handlePreviewClick = async () => {
        const response = await getCandidateFeedbackPDF(slug);
        downloadFile(response, 'candidate-feedback.pdf', 'application/pdf');
    };

    const handleRecipientCheckboxChange = key => async (e) => {
        const { checked } = e.target;
        let updated = { ...feedbackRecipients };
        if (key === 'all') {
            updated = {
                all: checked,
                completed: checked,
                hired: checked,
                rejected: checked
            };
        } else {
            updated[key] = checked;
            updated.all = updated.completed && updated.hired && updated.rejected;
        }
        setFeedbackRecipients(updated);
        const payload = {
            sendCandidateFeedbackToCompleted: updated.completed,
            sendCandidateFeedbackToHired: updated.hired,
            sendCandidateFeedbackToRejected: updated.rejected
        };
        setSaving(true);
        await saveAssessmentSettings(slug, payload);
        updateSettings(payload);
        setSaving(false);
    };

    const shouldDisableRecipient = (key) => {
        const selected = ['completed', 'hired', 'rejected'].filter(k => feedbackRecipients[k]).length;
        return !settingActive || !candidateFeedbackEnabled || (feedbackRecipients[key] && selected === 1);
    };
    const areAllIndividualRecipientsChecked = feedbackRecipients.completed && feedbackRecipients.hired && feedbackRecipients.rejected;

    return (
        <>
            <div className="u-dsp--distribute">
                <div className="u-mrg--bx2 u-dsp--f u-fw--wrap">
                    <Typography variant="h5" className="u-mrg--rx2">
                        Send Candidate Feedback
                        <InfoTooltip
                            id="send_candidate_feedback_id"
                            position="top"
                            isBlack
                            text="Feedback will only be sent for all new completions. Candidates who have previously received feedback would not receive feedback again."
                        />
                    </Typography>
                    <Button
                        color="primary"
                        onClick={handlePreviewClick}
                        className={clsx(classes.actionButton, 'u-mrg--rx2')}
                        disabled={!candidateFeedbackEnabled}
                    >
                        Preview PDF
                    </Button>
                    {!candidateFeedbackEnabled && (
                        <span className={classes.agreementText}>
                            {
                                hasEditPermissions
                                    ? (
                                        <>
                                            This setting must be authorized from the&nbsp;
                                            <Link
                                                to={{
                                                    pathname: '/company-account',
                                                    state: { scrollToEnd: true }
                                                }}
                                            >
                                                company account
                                            </Link>
                                        </>
                                    )
                                    : 'This setting must be authorized by your account admin'
                            }

                        </span>
                    )}
                </div>
                <Switch
                    onChange={handleSwitchChange}
                    checked={settingActive}
                    disabled={!candidateFeedbackEnabled}
                />
            </div>
            <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={feedbackVariant}
                onChange={handleRadioChange}
            >
                <FormControlLabel
                    value={RADIO.DAYS}
                    disabled={!settingActive || !candidateFeedbackEnabled}
                    control={<Radio />}
                    label={(
                        <div className="u-dsp--f u-ai--center">
                            <NumberInput
                                InputProps={{ classes: { input: classes.input } }}
                                variant="outlined"
                                min={3}
                                maxLength={30}
                                value={daysCount}
                                onChange={handleChangeDays}
                                fullWidth={false}
                                margin="none"
                                disabled={!settingActive || !candidateFeedbackEnabled}
                            />
                            <span className="u-mrg--lx2">days after last completion</span>
                        </div>
                    )}
                />
                <div className={classes.orLabel}>OR</div>
                <FormControlLabel disabled={!settingActive || !candidateFeedbackEnabled} value={RADIO.AFTER} control={<Radio />} label="Send Feedback after close of assessment" />
            </RadioGroup>
            <CandidateFeedbackButton audition={audition} isAgreement={candidateFeedbackEnabled} />
            <Checkbox
                label="Include Rank in Feedback"
                name="include_rank"
                onChange={handleIncludeCheckbox}
                checked={includeChecked}
                disabled={!candidateFeedbackEnabled}
            />
            <div className="u-mrg--tx2">
                <Typography variant="subtitle1" className="u-mrg--bx1 u-txt--bold">
                    Send Feedback to
                </Typography>
                <Checkbox
                    label="All Candidates"
                    name="send_to_all"
                    checked={feedbackRecipients.all}
                    onChange={handleRecipientCheckboxChange('all')}
                    disabled={!settingActive || !candidateFeedbackEnabled || areAllIndividualRecipientsChecked}
                    classes={{
                        label: 'u-txt--semibold'
                    }}
                />
                <div className="u-pdn--lx1">
                    <Checkbox
                        label="Completed Candidates (not including hired or rejected)"
                        name="send_to_completed"
                        checked={feedbackRecipients.completed}
                        onChange={handleRecipientCheckboxChange('completed')}
                        disabled={shouldDisableRecipient('completed')}
                    />
                    <Checkbox
                        label="Hired Candidates"
                        name="send_to_hired"
                        checked={feedbackRecipients.hired}
                        onChange={handleRecipientCheckboxChange('hired')}
                        disabled={shouldDisableRecipient('hired')}
                    />
                    <Checkbox
                        label="Rejected Candidates"
                        name="send_to_rejected"
                        checked={feedbackRecipients.rejected}
                        onChange={handleRecipientCheckboxChange('rejected')}
                        disabled={shouldDisableRecipient('rejected')}
                    />
                </div>
            </div>
        </>
    );
};

export default CandidateFeedback;
